import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useHistory } from 'react-router-dom';

import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Message from 'antd/lib/message';

import * as Yup from 'yup';

import moment from 'moment/moment';
import loadable from '@loadable/component';

import { loadableDelay } from '@common/react/loadable/loadableSettings';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { FormikPhoneControl } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import {
	emailValidator,
	notNullValidator,
	phoneRequiredValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';

import { Gender, GenderNames } from '@commonTuna/react/objects/Enums';
import { Device } from '@commonTuna/react/objects/Device';
import { useMobileView } from '@commonTuna/react/utils/useMobileView';

import { LocationPortal } from '@app/objects/CompanyPortal';
import { getLocationName } from '@app/components/Utils';
import { Doctor } from '@app/objects/Doctor';
import DatePickerLoader from '@app/components/UI/DatePickerLoader';
import { User } from '@app/objects/User';

import '@app/scss/components/inquiryForm.scss';

const DatePickerReact: any = loadable(
	() =>
		loadableDelay(import(/* webpackChunkName: "CustomDatePickerReact" */ '@common/react/components/UI/DatePickerReact/DatePickerReact') as any),
	{
		fallback: <DatePickerLoader placeholder="Date..." />,
		resolveComponent: (components: any) => components.DatePickerReact,
	},
);

interface InquiryFormValues {
	id: number;
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
	text: string;
	gender: Gender | null;
	locationId: number | null;
	doctorId: number;
}

interface InquiryFormProps {
	doctor?: Doctor;
	doctorId?: number;
	locationId?: number;
	doctors?: Array<Doctor>;
	locations: Array<LocationPortal>;
	selectedLocationId?: number;
}

const getValidationSchema = (locationRequired: boolean) => Yup.object().shape({
	locationId: locationRequired ? notNullValidator : Yup.mixed().notRequired(),
	firstName: simpleStringValidator,
	lastName: simpleStringValidator,
	phone: phoneRequiredValidator,
	email: emailValidator,
	doctorId: notNullValidator,
});

const year = new Date().getFullYear();

const years = Array.from({ length: 83 }).map((_, i) => year - 100 + i);

const InquiryForm: React.FC<InquiryFormProps> = ({
	doctor,
	doctors,
	locationId,
	doctorId,
	locations,
	selectedLocationId,
}) => {
	const { getUser } = useApplicationContext();
	const user = getUser<User>();

	const [success, setSuccess] = React.useState(false);
	const timer = React.useRef<any>(null);
	const [data, setData] = React.useState<InquiryFormValues>();

	const isMobile = useMobileView();
	const history = useHistory();

	const showDateOfBirth = locations?.[0]?.company?.showDateOfBirth;
	React.useEffect(() => {
		return () => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
	}, []);

	const locationRequired = !!locations?.length;

	const validationSchema = getValidationSchema(locationRequired);

	return (
		<div className="inquiry-form">
			<ItemProvider<InquiryFormValues>
				key={user?.id}
				id={-1}
				type="sendInquiry"
				onSaveRequestError={() => Message.error('Something went wrong!')}
				clearForSubmit={(values) => ({
					...values,
					name: `${values.firstName} ${values.lastName}`,
				})}
				validationSchema={validationSchema}
			>
				<ItemEditor
					readonly={false}
					withButtons
					beforeSubmit={(values, actions, submit) => {
						setData(values as any);
						submit()
							.then(() => {
								actions.resetForm();
								setSuccess(true);
								timer.current = setTimeout(() => {
									setSuccess(false);
									timer.current = null;
								}, user ? 5000 : 30000);
							});
					}}
					successMessage=""
					formikProps={{
						enableReinitialize: true,
					}}
					saveText="Submit"
					getInitialValues={() => ({
						firstName: user?.firstName || '',
						lastName: user?.lastName || '',
						phone: user?.phoneNumber || '',
						email: user?.email || '',
						text: '',
						gender: user?.gender ?? Gender.Female,
						locationId: locationId || selectedLocationId || (locations?.length ? locations[0].id : null),
						doctorId,
						device: isMobile ? Device.Mobile : Device.Desktop,
						path: history.location.pathname,
						birthDate: user?.birthDate,
					})}
					edit={() => <>
						<div className="row">
							<FormikField
								title="First Name*"
								fieldName="firstName"
								inputProps={{
									maxLength: 50,
								}}
								containerClassName="form-group col-sm-12"
							/>
						</div>
						<div className="row">
							<FormikField
								title="Last Name*"
								fieldName="lastName"
								inputProps={{
									maxLength: 50,
								}}
								containerClassName="form-group col-sm-12"
							/>
						</div>
						<div className="row">
							<FormikField
								fieldName="phone"
								title="Phone Number*"
								containerClassName={`form-group col-sm-${showDateOfBirth ? '6' : '12'}`}
								render={(fieldProps) =>
									<FormikPhoneControl
										fieldProps={fieldProps}
										placeholder="+1 (___) ___-____"
									/>
								}
							/>
							<FormikField
								title="Email*"
								fieldName="email"
								containerClassName={`form-group col-sm-${showDateOfBirth ? '6' : '12'}`}
							/>
						</div>
						<div className="row">
							<FormikField
								title="Gender*"
								fieldName="gender"
								containerClassName={`form-group col-sm-${showDateOfBirth ? '6' : '12'}`}
								render={({ field, form }) => <>
									<Select
										placeholder="Gender"
										defaultValue={Gender.Female}
										onChange={(value) => {
											form.setFieldValue(field.name, +value);
										}}
										value={GenderNames[field.value]}
									>

										{Object.keys(GenderNames).map((key) =>
											<Select.Option value={key} key={key}>{GenderNames[key]}</Select.Option>)}
									</Select>
								</>
								}
							/>
							{showDateOfBirth
								&& <FormikField
									fieldName="birthDate"
									title="Birth Date"
									containerClassName="form-group col-sm-6"
									render={({ field, form }) => (
										<DatePickerReact
											value={field.value}
											yearsSelectOptions={years}
											isMobile={false}
											onChange={(date) => form.setFieldValue(field.name, date)}
											datePickerProps={{
												showMonthDropdown: true,
												showYearDropdown: true,
												dropdownMode: 'select',
												maxDate: new Date(+new Date() - 18 * 365 * 24 * 1000 * 3600),
												minDate: moment().add(-100, 'years').toDate(),
												popperClassName: 'custom-date-picker-container',
												popperContainer: typeof window !== 'undefined'
													? ({ children }) => createPortal(children, document.body) : undefined,
											}}
											utc
										/>
									)}
								/>}
						</div>
						{doctor ? <div className="row">
							<FormikField
								title="Doctor"
								fieldName="doctorId"
								containerClassName="form-group col-sm-12"
								render={({ field, form }) =>
									<Select
										placeholder="Doctor"
										value={field.value}
									>
										<Select.Option key={`location-${doctor.id}`} value={doctor.id}>
											{doctor?.nameEn}
										</Select.Option>
									</Select>
								}
							/>
						</div> : null}
						{locations ? <div className="row">
							<FormikField
								title={<>
									Location
									{locationRequired ? '*' : ''}
								</> as any}
								fieldName="locationId"
								containerClassName="form-group col-sm-12"
								render={({ field, form }) =>
									<Select
										placeholder="Location"
										onChange={(value) => form.setFieldValue(field.name, value)}
										value={field.value}
									>
										{locations?.map((location) => <Select.Option key={`location-${location.id}`} value={location.id}>
											{getLocationName(location)}
										</Select.Option>)}
									</Select>
								}
							/>
						</div> : null}
						{doctors ? <div className="row">
							<FormikField
								title="Doctor*"
								fieldName="doctorId"
								containerClassName="form-group col-sm-12"
								render={({ field, form }) =>
									<Select
										placeholder="Doctor"
										onChange={(value) => form.setFieldValue(field.name, value)}
										value={field.value}
									>
										{doctors?.map((doctor) => <Select.Option key={`doctor-${doctor.id}`} value={doctor.id}>
											{doctor.nameEn}
										</Select.Option>)}
									</Select>
								}
							/>
						</div> : null}
						<div className="row">
							<FormikField
								title="Text"
								fieldName="text"
								containerClassName="form-group col-sm-12"
								render={({ field }) =>
									<Input.TextArea
										className="form-control"
										maxLength={500}
										style={{ height: 90, resize: 'none' }}
										{...field}
									/>
								}
							/>
						</div>
					</>}
				/>
			</ItemProvider>
			{success ? <div className="inquiry-form__success">
				<div>
					<i className="fa fa-check" />
					<h2>
						success
					</h2>
				</div>
				<div>
					{/* Your request has been sent successfully. */}
					{user ? <>
						<p>
							Thank you! Clinic front desk will contact you as soon as possible.
						</p>
						<Link
							className="btn btn-primary"
							to={{ pathname: '/doctors' }}
						>
							Find More Providers
						</Link>
					</>
						: <>
							<p>
								Complete quick registration to get even more features.
							</p>
							<Link
								className="btn btn-primary"
								to={{ pathname: '/registration', state: { data } }}
							>
								Registration
							</Link>
							{/*
							{' '}
							<Link
								to={{ pathname: '/about-registration', state: { data } }}
							>

								Why I need Registration?
							</Link>
							*/}
						</>
					}
				</div>
			</div> : null}
		</div>
	);
};

export default InquiryForm;
